import { Component } from '@grafikr/components';

import { on } from 'utils/theme';

/**
 * Size chart / Fit guide drawer.
 * Tabs + fit-card video play/pause (mirrors fit-video-slider play/pause, no Swiper).
 *
 * @example
 * <div data-component="v2-size-guide" data-active-tab="sizing-chart">...</div>
 */
const playVideos = (videos: HTMLVideoElement[]) => {
  videos.forEach((video) => {
    video.muted = true;
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => {});
    }
  });
};

const pauseVideos = (videos: HTMLVideoElement[]) => {
  videos.forEach((video) => {
    video.pause();
  });
};

const getPlayableVideos = (card: HTMLElement) => {
  const videos = [...card.querySelectorAll('video')];
  if (videos.length === 0) return [];

  const visibleVideos = videos.filter((video) => video.offsetParent !== null);
  return visibleVideos.length > 0 ? visibleVideos : videos;
};

const updateButton = (button: HTMLElement | null, paused: boolean) => {
  if (!button) return;
  button.setAttribute('aria-pressed', paused ? 'true' : 'false');
  button.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');

  const pauseIcon = button.querySelector('[data-size-guide-video-icon="pause"]');
  const playIcon = button.querySelector('[data-size-guide-video-icon="play"]');

  if (pauseIcon) pauseIcon.classList.toggle('hidden', paused);
  if (playIcon) playIcon.classList.toggle('hidden', !paused);
};

const setCardPaused = (card: HTMLElement, paused: boolean) => {
  card.dataset.sizeGuideVideoPaused = paused ? 'true' : 'false';
  const button = card.querySelector<HTMLElement>('[data-size-guide-video-toggle]');
  updateButton(button, paused);

  if (paused) {
    pauseVideos([...card.querySelectorAll('video')]);
  } else {
    playVideos(getPlayableVideos(card));
  }
};

const setActiveTab = (node: HTMLElement, tab: string) => {
  const nextTab = tab === 'fit-guide' ? 'fit-guide' : 'sizing-chart';

  if (node.dataset.activeTab !== nextTab) {
    node.dataset.activeTab = nextTab;
  }

  node.querySelectorAll<HTMLElement>('[data-size-guide-tab]').forEach((button) => {
    const selected = button.dataset.sizeGuideTab === nextTab;
    button.setAttribute('aria-selected', selected ? 'true' : 'false');
    button.setAttribute('tabindex', '0');
  });

  node.querySelectorAll<HTMLElement>('[data-size-guide-panel]').forEach((panel) => {
    const active = panel.dataset.sizeGuidePanel === nextTab;
    panel.setAttribute('aria-hidden', active ? 'false' : 'true');
  });
};

export default Component((node) => {
  const modal = node.closest<HTMLElement>('[data-modal]');
  const cards = [...node.querySelectorAll<HTMLElement>('[data-size-guide-media-card]')];
  const tabs = [...node.querySelectorAll<HTMLElement>('[data-size-guide-tab]')];

  setActiveTab(node, node.dataset.activeTab || 'sizing-chart');

  node.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const tabButton = target.closest<HTMLElement>('[data-size-guide-tab]');
    if (tabButton && node.contains(tabButton)) {
      setActiveTab(node, tabButton.dataset.sizeGuideTab || 'sizing-chart');
      return;
    }

    const videoToggle = target.closest<HTMLElement>('[data-size-guide-video-toggle]');
    if (!videoToggle || !node.contains(videoToggle)) return;

    const card = videoToggle.closest<HTMLElement>('[data-size-guide-media-card]');
    if (!card) return;

    const paused = card.dataset.sizeGuideVideoPaused === 'true';
    setCardPaused(card, !paused);
  });

  node.addEventListener('focusin', (event) => {
    const target = event.target as HTMLElement | null;
    const tabButton = target?.closest<HTMLElement>('[data-size-guide-tab]');
    if (!tabButton || !node.contains(tabButton)) return;
    setActiveTab(node, tabButton.dataset.sizeGuideTab || 'sizing-chart');
  });

  node.addEventListener('keydown', (event) => {
    const target = event.target as HTMLElement | null;
    const tabButton = target?.closest<HTMLElement>('[data-size-guide-tab]');
    if (!tabButton || !node.contains(tabButton) || tabs.length < 2) return;

    const index = tabs.indexOf(tabButton);
    if (index < 0) return;

    let nextIndex = -1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (index + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (index - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const next = tabs[nextIndex];
    next.focus();
    setActiveTab(node, next.dataset.sizeGuideTab || 'sizing-chart');
  });

  cards.forEach((card) => {
    if (card.querySelector('video')) {
      setCardPaused(card, true);
    }
  });

  const syncPlayback = () => {
    const isOpen = !!modal && modal.classList.contains('open') && !modal.hasAttribute('hidden');
    cards.forEach((card) => {
      if (!card.querySelector('video')) return;
      setCardPaused(card, !isOpen);
    });
  };

  if (modal) {
    const observer = new MutationObserver(() => {
      syncPlayback();
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['class', 'hidden'] });
    syncPlayback();
  }

  on('pdp-open-size-guide', (event: Event) => {
    if (!modal || !modal.classList.contains('open')) return;
    const detail = (event as CustomEvent<{ tab?: string }>).detail;
    if (detail?.tab) {
      setActiveTab(node, detail.tab);
    }
    syncPlayback();
  });
});
